/**
 * Money — shared OFFICE cost (docs/01 §7). Bills, the one ledger, settle-up,
 * and food orders.
 *
 * Five rules run through everything here:
 *
 *  1. **Integer piastres, always.** No floats, no rounding drift, no
 *     `toFixed`. Every allocation in this file is exact: the shares of a split
 *     sum to the total by construction, not by luck.
 *  2. **Bill status is derived, never stored** (§7.1). A bill instance has many
 *     payments; `paid`, `outstanding`, `status` and `overdue` are all computed.
 *  3. **One ledger, not two** (§7.2). A bill payment produces a shared expense,
 *     so paying the electricity and splitting lunch land in the same place.
 *  4. **Participants are chosen per expense**, never assumed to be all three.
 *  5. **Guests affect the arithmetic and then disappear.** A guest's share is
 *     marked `settledImmediately` and is excluded from every balance.
 *
 * Purity: no `Date.now()`, no globals, no mutation of inputs. Every function
 * that needs the time takes an explicit `now: number` of corrected UTC epoch
 * milliseconds, so two offline devices compute identical results with no
 * coordination (docs/02 §8).
 */

import type {
  BillInstanceId,
  ExpenseId,
  MemberId,
  PersonId,
} from './ids.js'
import { asId } from './ids.js'
import type { Piastres } from './units.js'
import { piastres } from './units.js'
import type { DayKey } from './time.js'
import { DEFAULT_ROLLOVER_HOUR, daysBetween, rolloverDayBounds, rolloverDayOf } from './time.js'
import type {
  Bill,
  BillCycle,
  BillInstance,
  BillInstanceView,
  BillPayment,
  BillStatus,
  Expense,
  ExpenseSplit,
  FoodOrder,
  PairBalance,
  Settlement,
  SplitKind,
} from './entities/money.js'

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export type MoneyErrorCode =
  | 'EMPTY_PARTICIPANTS'
  | 'DUPLICATE_PARTICIPANT'
  | 'MISSING_EXACT_AMOUNT'
  | 'EXACT_SUM_MISMATCH'
  | 'MISSING_SHARES'
  | 'BAD_SHARES'
  | 'ZERO_SHARES'
  | 'NOT_AN_INTEGER'
  | 'BAD_DAY_KEY'
  | 'BAD_DUE_DAY'
  | 'RANGE_TOO_LARGE'
  | 'NO_PAYER'
  | 'NEGATIVE_AMOUNT'
  | 'DISCOUNT_EXCEEDS_TOTAL'
  | 'UNKNOWN_SPLIT_KIND'
  | 'MISMATCHED_REF'

/** Thrown for caller mistakes. Never used for ordinary domain outcomes. */
export class MoneyError extends Error {
  readonly code: MoneyErrorCode
  constructor(code: MoneyErrorCode, message: string) {
    super(`${code}: ${message}`)
    this.name = 'MoneyError'
    this.code = code
  }
}

// ---------------------------------------------------------------------------
// Integer money primitives
// ---------------------------------------------------------------------------

export const ZERO: Piastres = piastres(0)

/** Sum of integer piastres. Empty sums to zero. */
export function sumPiastres(xs: Iterable<Piastres>): Piastres {
  let total = 0
  for (const x of xs) total += x
  return piastres(total)
}

function assertInt(n: number, what: string): number {
  if (!Number.isSafeInteger(n)) {
    throw new MoneyError('NOT_AN_INTEGER', `${what} must be a safe integer, got ${n}`)
  }
  return n
}

/**
 * Split `total` into `n` shares that differ by at most one piastre and sum
 * EXACTLY to `total`. The remainder goes to the first |rem| participants, so
 * the result is a pure function of (total, n) and of participant order —
 * identical on every device with no coordination.
 *
 * Negative totals (a refund) are handled symmetrically: the first participants
 * absorb the extra negative piastre.
 */
export function allocateEqually(total: Piastres, n: number): Piastres[] {
  if (!Number.isSafeInteger(n) || n <= 0) {
    throw new MoneyError('EMPTY_PARTICIPANTS', `cannot split among ${n} participants`)
  }
  assertInt(total, 'total')
  // `%` and the subtraction are exact for safe integers; `total / n` alone is
  // not, so we never divide before removing the remainder.
  const rem = total % n
  const base = (total - rem) / n
  const step = rem >= 0 ? 1 : -1
  const extra = Math.abs(rem)
  const out: Piastres[] = []
  for (let i = 0; i < n; i++) out.push(piastres(base + (i < extra ? step : 0)))
  return out
}

/**
 * Largest-remainder (Hamilton) allocation of `total` across integer `weights`.
 * Sums EXACTLY to `total`. Ties on the fractional remainder are broken by
 * ascending index, so it is fully deterministic.
 *
 * A zero weight always receives exactly zero: the leftover units can only go to
 * entries with a non-zero fractional part, and those all have a non-zero weight.
 *
 * The allocation runs on the MAGNITUDE of the total and takes the sign
 * afterwards. That matters for a refund: allocating -7 directly would floor
 * everyone to -3 and hand the two leftover units to the first indices, leaving
 * `[-2, -2, -3]` — the LAST participant absorbing the extra negative piastre,
 * the opposite of `allocateEqually`, and the opposite of the "larger weight
 * takes the larger share" reading. Going through the magnitude makes the two
 * primitives agree exactly on uniform weights, for every total of either sign.
 */
export function allocateProportionally(total: Piastres, weights: readonly number[]): Piastres[] {
  const n = weights.length
  if (n === 0) throw new MoneyError('EMPTY_PARTICIPANTS', 'cannot allocate across zero weights')
  assertInt(total, 'total')
  let totalWeight = 0
  for (const w of weights) {
    assertInt(w, 'weight')
    if (w < 0) throw new MoneyError('BAD_SHARES', `weights must be non-negative, got ${w}`)
    totalWeight += w
  }
  if (totalWeight === 0) {
    throw new MoneyError('ZERO_SHARES', 'weights sum to zero; nothing to allocate against')
  }

  const sign = total < 0 ? -1 : 1
  const magnitude = Math.abs(total)

  const quota: number[] = new Array<number>(n)
  const remainder: number[] = new Array<number>(n)
  let assigned = 0
  for (let i = 0; i < n; i++) {
    const num = magnitude * (weights[i] as number)
    assertInt(num, 'total x weight')
    let q = Math.floor(num / totalWeight)
    // Guard the one place floating-point division could land off by one.
    let r = num - q * totalWeight
    if (r < 0) {
      q -= 1
      r += totalWeight
    } else if (r >= totalWeight) {
      q += 1
      r -= totalWeight
    }
    quota[i] = q
    remainder[i] = r
    assigned += q
  }

  const left = magnitude - assigned // 0 <= left < n, by construction
  const order = Array.from({ length: n }, (_, i) => i).sort((a, b) => {
    const ra = remainder[a] as number
    const rb = remainder[b] as number
    return rb - ra || a - b
  })
  for (let k = 0; k < left; k++) {
    const i = order[k] as number
    quota[i] = (quota[i] as number) + 1
  }
  // `sign * 0` is -0, and Math.round preserves it. A stored -0 survives locally
  // but JSON.stringify writes "0", so the synced copy and the local one would
  // deep-compare unequal for the rest of time. Normalise it away at the source.
  return quota.map(q => piastres(q === 0 ? 0 : sign * q))
}

// ---------------------------------------------------------------------------
// Person / member identity
// ---------------------------------------------------------------------------

/**
 * `Member` carries both a `PersonId` (the shared people row) and a `MemberId`.
 * They are the same underlying UUID — `members` extends `people` (docs/01 §1.1)
 * — but they are separately branded, so crossing between them is explicit and
 * greppable rather than accidental.
 */
export const personIdOf = (m: MemberId): PersonId => asId<PersonId>(m)
export const memberIdOf = (p: PersonId): MemberId => asId<MemberId>(p)

// ---------------------------------------------------------------------------
// Splitting
// ---------------------------------------------------------------------------

export interface SplitParticipant {
  readonly personId: PersonId
  /**
   * Guests never carry an ongoing balance (docs/01 §7.2): their share affects
   * the split arithmetic and is then excluded from the ledger entirely.
   */
  readonly settledImmediately?: boolean
  /** Required when kind is 'exact'. Ignored otherwise. */
  readonly exactAmount?: Piastres
  /** Required when kind is 'shares'. Non-negative integer weight. */
  readonly shares?: number
}

export const participant = (personId: PersonId): SplitParticipant => ({ personId })
export const guestParticipant = (personId: PersonId): SplitParticipant => ({
  personId,
  settledImmediately: true,
})

function assertDistinct(participants: readonly SplitParticipant[]): void {
  if (participants.length === 0) {
    throw new MoneyError('EMPTY_PARTICIPANTS', 'an expense needs at least one participant')
  }
  const seen = new Set<string>()
  for (const p of participants) {
    if (seen.has(p.personId)) {
      throw new MoneyError('DUPLICATE_PARTICIPANT', `${p.personId} appears twice in the split`)
    }
    seen.add(p.personId)
  }
}

/**
 * Split `amount` across `participants`. Participants are chosen PER EXPENSE and
 * are never assumed to be all three owners (docs/01 §7.2).
 *
 * The returned shares ALWAYS sum exactly to `amount`, for every kind:
 *   - 'equal'  — even shares, remainder to the first participants
 *   - 'exact'  — caller-supplied amounts, validated to sum to `amount`
 *   - 'shares' — weighted, largest-remainder, exact
 */
export function splitExpense(
  amount: Piastres,
  kind: SplitKind,
  participants: readonly SplitParticipant[],
): ExpenseSplit[] {
  assertDistinct(participants)
  assertInt(amount, 'amount')

  let shares: Piastres[]
  switch (kind) {
    case 'equal':
      shares = allocateEqually(amount, participants.length)
      break
    case 'exact': {
      const exact: Piastres[] = []
      for (const p of participants) {
        if (p.exactAmount === undefined) {
          throw new MoneyError(
            'MISSING_EXACT_AMOUNT',
            `exact split needs an amount for ${p.personId}`,
          )
        }
        assertInt(p.exactAmount, 'exactAmount')
        exact.push(p.exactAmount)
      }
      const total = sumPiastres(exact)
      if (total !== amount) {
        throw new MoneyError(
          'EXACT_SUM_MISMATCH',
          `exact shares sum to ${total}, expense is ${amount}`,
        )
      }
      shares = exact
      break
    }
    case 'shares': {
      const weights: number[] = []
      for (const p of participants) {
        if (p.shares === undefined) {
          throw new MoneyError('MISSING_SHARES', `shares split needs a weight for ${p.personId}`)
        }
        weights.push(p.shares)
      }
      shares = allocateProportionally(amount, weights)
      break
    }
    default: {
      const never: never = kind
      throw new MoneyError('UNKNOWN_SPLIT_KIND', `unknown split kind ${String(never)}`)
    }
  }

  return participants.map((p, i) => ({
    personId: p.personId,
    shareAmount: shares[i] as Piastres,
    settledImmediately: p.settledImmediately === true,
  }))
}

/** Total of a split. Invariant: equals the expense amount for a well-formed expense. */
export const splitTotal = (splits: readonly ExpenseSplit[]): Piastres =>
  sumPiastres(splits.map(s => s.shareAmount))

/** True when the splits account for the whole amount, which they always should. */
export const isBalancedExpense = (e: Expense): boolean => splitTotal(e.splits) === e.amount

// ---------------------------------------------------------------------------
// DayKey helpers (construction; all reckoning still comes from time.ts)
// ---------------------------------------------------------------------------

const p2 = (n: number): string => String(n).padStart(2, '0')

export function parseDayKey(day: DayKey): { year: number; month: number; day: number } {
  const parts = day.split('-')
  const year = Number(parts[0])
  const month = Number(parts[1])
  const dayOfMonth = Number(parts[2])
  if (
    parts.length !== 3 ||
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(dayOfMonth) ||
    month < 1 ||
    month > 12 ||
    dayOfMonth < 1 ||
    dayOfMonth > 31
  ) {
    throw new MoneyError('BAD_DAY_KEY', `not a DayKey: ${String(day)}`)
  }
  return { year, month, day: dayOfMonth }
}

/** Build a DayKey from calendar components. Month/day overflow normalises. */
export function dayKeyOf(year: number, month1: number, dayOfMonth: number): DayKey {
  const d = new Date(Date.UTC(year, month1 - 1, dayOfMonth))
  return `${d.getUTCFullYear()}-${p2(d.getUTCMonth() + 1)}-${p2(d.getUTCDate())}` as DayKey
}

/** Length of a calendar month. February is 29 in a leap year. month1 is 1-12. */
export function daysInMonth(year: number, month1: number): number {
  return new Date(Date.UTC(year, month1, 0)).getUTCDate()
}

/** Clamp a day-of-month to the month's real length: 31 in February becomes 28 or 29. */
export function clampDayOfMonth(dayOfMonth: number, year: number, month1: number): number {
  return Math.min(dayOfMonth, daysInMonth(year, month1))
}

const dayLte = (a: DayKey, b: DayKey): boolean => daysBetween(a, b) >= 0

// ---------------------------------------------------------------------------
// Bills — status is DERIVED, never stored (docs/01 §7.1)
// ---------------------------------------------------------------------------

/**
 * ```
 * outstanding <= 0            -> 'paid'
 * 0 < paid < amountDue        -> 'partial'
 * paid == 0                   -> 'unpaid'
 * ```
 * The spec's three cases do not cover a negative `paid` (a refund recorded as a
 * negative payment), so the rule is applied in the order above and everything
 * that is neither fully paid nor positively part-paid reads 'unpaid'.
 */
export function billStatus(amountDue: Piastres, paid: Piastres): BillStatus {
  if (amountDue - paid <= 0) return 'paid'
  if (paid <= 0) return 'unpaid'
  return 'partial'
}

/**
 * The full derived view of one bill instance.
 *
 * A bill instance can have MANY payments; overpayment is kept honest —
 * `outstanding` goes negative and the status is 'paid'.
 *
 * `overdue` uses the office's rollover reckoning (docs/01 ⚑): a bill due on a
 * given DayKey is not overdue until that office day has fully elapsed, so
 * paying it at 02:00 the following calendar night is still on time.
 */
export function billInstanceView(
  instance: BillInstance,
  payments: readonly BillPayment[],
  now: number,
  rolloverHour: number = DEFAULT_ROLLOVER_HOUR,
): BillInstanceView {
  const mine = payments
    .filter(p => p.billInstanceId === instance.id)
    .slice()
    .sort((a, b) => a.paidAt - b.paidAt || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
  const paid = sumPiastres(mine.map(p => p.amount))
  const outstanding = piastres(instance.amountDue - paid)
  const status = billStatus(instance.amountDue, paid)
  const { endUtc } = rolloverDayBounds(instance.dueDay, rolloverHour)
  return {
    instance,
    payments: mine,
    paid,
    outstanding,
    status,
    overdue: now >= endUtc && outstanding > 0,
  }
}

export interface BillHistory {
  bill: Bill
  /** Every known instance of this bill, oldest due date first. */
  views: BillInstanceView[]
  totalDue: Piastres
  totalPaid: Piastres
  /** Sum of positive outstandings; overpaid instances do not offset unpaid ones. */
  totalOutstanding: Piastres
  overdueCount: number
}

/** Per bill: every instance with what was due, what was paid, when, and by whom. */
export function billHistory(
  bill: Bill,
  instances: readonly BillInstance[],
  payments: readonly BillPayment[],
  now: number,
  rolloverHour: number = DEFAULT_ROLLOVER_HOUR,
): BillHistory {
  const views = instances
    .filter(i => i.billId === bill.id)
    .slice()
    .sort((a, b) => (a.dueDay < b.dueDay ? -1 : a.dueDay > b.dueDay ? 1 : a.id < b.id ? -1 : 1))
    .map(i => billInstanceView(i, payments, now, rolloverHour))
  return {
    bill,
    views,
    totalDue: sumPiastres(views.map(v => v.instance.amountDue)),
    totalPaid: sumPiastres(views.map(v => v.paid)),
    totalOutstanding: piastres(
      views.reduce((acc, v) => acc + Math.max(0, v.outstanding), 0),
    ),
    overdueCount: views.filter(v => v.overdue).length,
  }
}

// ---------------------------------------------------------------------------
// Instance materialisation — 12 months ahead, idempotent
// ---------------------------------------------------------------------------

export const MATERIALISE_MONTHS_AHEAD = 12

/**
 * The window instances are materialised over: the START OF THE CURRENT OFFICE
 * MONTH through 12 months ahead.
 *
 * It deliberately does NOT start at today. A bill added on the 5th whose due day
 * is the 1st still owes this month — starting the window at today would skip the
 * current period permanently, because the next materialisation run starts later
 * still and the period's due day only recedes. Reaching back to the 1st costs at
 * most one extra instance, is idempotent (the natural key dedupes it on every
 * later run), and is what makes "history and future are both browsable"
 * (docs/01 §7.1) true of the period you are actually living in.
 */
export function billMaterialisationHorizon(
  now: number,
  rolloverHour: number = DEFAULT_ROLLOVER_HOUR,
): { fromDay: DayKey; toDay: DayKey } {
  const today = rolloverDayOf(now, rolloverHour)
  const { year, month, day } = parseDayKey(today)
  const toYear = year + Math.floor((month - 1 + MATERIALISE_MONTHS_AHEAD) / 12)
  const toMonth = ((month - 1 + MATERIALISE_MONTHS_AHEAD) % 12) + 1
  return {
    fromDay: dayKeyOf(year, month, 1),
    toDay: dayKeyOf(toYear, toMonth, clampDayOfMonth(day, toYear, toMonth)),
  }
}

export interface BillPeriod {
  periodLabel: string
  dueDay: DayKey
}

/** Factory for the id of a newly materialised instance (uuidv7 needs a clock and rand). */
export type BillInstanceIdFactory = (period: BillPeriod, bill: Bill) => BillInstanceId

function cycleStepMonths(cycle: BillCycle): number {
  switch (cycle) {
    case 'monthly':
      return 1
    case 'quarterly':
      return 3
    case 'yearly':
      return 12
    case 'once':
      return 0
  }
}

function periodLabelFor(cycle: BillCycle, year: number, month1: number): string {
  switch (cycle) {
    case 'monthly':
    case 'once':
      return `${year}-${p2(month1)}`
    case 'quarterly':
      return `${year}-Q${Math.floor((month1 - 1) / 3) + 1}`
    case 'yearly':
      return `${year}`
  }
}

const MAX_PERIOD_MONTHS = 12_000

/**
 * Which periods of a bill fall due within [fromDay, toDay], inclusive.
 *
 * The cycle is phased on the bill's creation month in the office's rollover
 * reckoning, so a quarterly bill created in February falls due in Feb/May/Aug/Nov
 * rather than on calendar quarters. The due day of month is CLAMPED to the
 * month's real length: `dueDayOfMonth: 31` is the 28th in February, the 29th in
 * a leap February, and the 30th in April.
 */
export function billPeriods(
  bill: Bill,
  fromDay: DayKey,
  toDay: DayKey,
  rolloverHour: number = DEFAULT_ROLLOVER_HOUR,
): BillPeriod[] {
  if (!Number.isInteger(bill.dueDayOfMonth) || bill.dueDayOfMonth < 1 || bill.dueDayOfMonth > 31) {
    throw new MoneyError('BAD_DUE_DAY', `dueDayOfMonth must be 1-31, got ${bill.dueDayOfMonth}`)
  }
  if (!dayLte(fromDay, toDay)) return []

  const from = parseDayKey(fromDay)
  const to = parseDayKey(toDay)
  const fromMi = from.year * 12 + (from.month - 1)
  const toMi = to.year * 12 + (to.month - 1)
  if (toMi - fromMi > MAX_PERIOD_MONTHS) {
    throw new MoneyError('RANGE_TOO_LARGE', `${fromDay}..${toDay} spans too many months`)
  }

  const anchorDay = rolloverDayOf(bill.createdAt, rolloverHour)
  const anchor = parseDayKey(anchorDay)
  const anchorMi = anchor.year * 12 + (anchor.month - 1)

  const dueDayFor = (mi: number): DayKey => {
    const y = Math.floor(mi / 12)
    const m = (mi % 12) + 1
    return dayKeyOf(y, m, clampDayOfMonth(bill.dueDayOfMonth, y, m))
  }
  const periodFor = (mi: number): BillPeriod => {
    const y = Math.floor(mi / 12)
    const m = (mi % 12) + 1
    return { periodLabel: periodLabelFor(bill.cycle, y, m), dueDay: dueDayFor(mi) }
  }

  if (bill.cycle === 'once') {
    // A one-off falls due on the first occurrence of dueDayOfMonth on or after
    // the day the bill was created.
    let mi = anchorMi
    if (!dayLte(anchorDay, dueDayFor(mi))) mi += 1
    const period = periodFor(mi)
    return dayLte(fromDay, period.dueDay) && dayLte(period.dueDay, toDay) ? [period] : []
  }

  const step = cycleStepMonths(bill.cycle)
  // Walk a month either side of the window: a period's month can sit inside the
  // window while its clamped due day sits outside it, and vice versa.
  const scanFrom = fromMi - step
  const offset = (((scanFrom - anchorMi) % step) + step) % step
  const start = scanFrom + ((step - offset) % step)

  const out: BillPeriod[] = []
  for (let mi = start; mi <= toMi + step; mi += step) {
    const period = periodFor(mi)
    if (dayLte(fromDay, period.dueDay) && dayLte(period.dueDay, toDay)) out.push(period)
  }
  return out
}

export interface GeneratedBillInstances {
  /** Every known instance of this bill after materialisation, due date ascending. */
  instances: BillInstance[]
  /** Only the ones this call materialised. Empty on a replay. */
  created: BillInstance[]
}

/**
 * Materialise a bill's instances over [fromDay, toDay] so history and future are
 * both browsable offline (docs/01 §7.1).
 *
 * Idempotent: the natural key is (billId, periodLabel), matching the sync
 * strategy in docs/02 §8. Feeding the returned `instances` straight back in
 * creates nothing. Inactive bills materialise nothing but keep what exists.
 */
export function generateBillInstances(
  bill: Bill,
  fromDay: DayKey,
  toDay: DayKey,
  existing: readonly BillInstance[],
  newId: BillInstanceIdFactory,
  rolloverHour: number = DEFAULT_ROLLOVER_HOUR,
): GeneratedBillInstances {
  const mine = existing.filter(i => i.billId === bill.id)
  const byLabel = new Map<string, BillInstance>()
  for (const i of mine) if (!byLabel.has(i.periodLabel)) byLabel.set(i.periodLabel, i)

  const created: BillInstance[] = []
  if (bill.active) {
    for (const period of billPeriods(bill, fromDay, toDay, rolloverHour)) {
      if (byLabel.has(period.periodLabel)) continue
      const instance: BillInstance = {
        id: newId(period, bill),
        billId: bill.id,
        periodLabel: period.periodLabel,
        dueDay: period.dueDay,
        amountDue: bill.amountExpected,
      }
      byLabel.set(period.periodLabel, instance)
      created.push(instance)
    }
  }

  const instances = [...mine, ...created].sort((a, b) =>
    a.dueDay < b.dueDay
      ? -1
      : a.dueDay > b.dueDay
        ? 1
        : a.periodLabel < b.periodLabel
          ? -1
          : a.periodLabel > b.periodLabel
            ? 1
            : a.id < b.id
              ? -1
              : a.id > b.id
                ? 1
                : 0,
  )
  return { instances, created }
}

// ---------------------------------------------------------------------------
// One ledger: a bill payment IS a shared expense (docs/01 §7.2)
// ---------------------------------------------------------------------------

export interface BillPaymentExpenseOptions {
  expenseId: ExpenseId
  /** Used when `bill.splitAmong` is empty — "empty means all active owners". */
  activeOwners?: readonly MemberId[]
  /** Full override, e.g. to include a guest or use unequal shares. */
  participants?: readonly SplitParticipant[]
  splitKind?: SplitKind
  description?: string
  category?: string
  /** Defaults to the payment's own timestamp. */
  createdAt?: number
}

/**
 * The expense created automatically by a bill payment. If one owner pays the
 * electricity in full, the others owe their share immediately — bills and the
 * shared ledger are one system, not two.
 *
 * The expense is for what was ACTUALLY PAID (`payment.amount`), not what was
 * due: a part-payment creates a part-sized shared expense, and a second payment
 * later creates a second one. That keeps the ledger equal to real cash moved.
 */
export function expenseFromBillPayment(
  bill: Bill,
  instance: BillInstance,
  payment: BillPayment,
  opts: BillPaymentExpenseOptions,
): Expense {
  // Cross-linked money records are a real hazard: without these two guards a
  // payment for March, attached to April's instance, would silently produce a
  // ledger entry labelled with the wrong period and the wrong bill's category.
  if (instance.billId !== bill.id) {
    throw new MoneyError(
      'MISMATCHED_REF',
      `instance ${instance.id} belongs to bill ${instance.billId}, not ${bill.id}`,
    )
  }
  if (payment.billInstanceId !== instance.id) {
    throw new MoneyError(
      'MISMATCHED_REF',
      `payment ${payment.id} belongs to instance ${payment.billInstanceId}, not ${instance.id}`,
    )
  }

  const participants: readonly SplitParticipant[] =
    opts.participants ??
    (bill.splitAmong.length > 0 ? bill.splitAmong : (opts.activeOwners ?? [])).map(m => ({
      personId: personIdOf(m),
    }))

  if (participants.length === 0) {
    throw new MoneyError(
      'EMPTY_PARTICIPANTS',
      `bill ${bill.name} has no splitAmong and no activeOwners were supplied`,
    )
  }

  const splitKind: SplitKind = opts.splitKind ?? 'equal'
  return {
    id: opts.expenseId,
    description: opts.description ?? `${bill.name} — ${instance.periodLabel}`,
    amount: payment.amount,
    spentAt: payment.paidAt,
    paidByMemberId: payment.paidByMemberId,
    category: opts.category ?? bill.category,
    splitKind,
    splits: splitExpense(payment.amount, splitKind, participants),
    source: 'bill_payment',
    sourceRef: payment.id,
    createdAt: opts.createdAt ?? payment.paidAt,
  }
}

export interface ManualExpenseInput {
  expenseId: ExpenseId
  description: string
  amount: Piastres
  spentAt: number
  paidByMemberId: MemberId
  category: string
  splitKind: SplitKind
  participants: readonly SplitParticipant[]
  createdAt?: number
}

/** A hand-entered expense. Same shape, same ledger. */
export function createExpense(input: ManualExpenseInput): Expense {
  return {
    id: input.expenseId,
    description: input.description,
    amount: input.amount,
    spentAt: input.spentAt,
    paidByMemberId: input.paidByMemberId,
    category: input.category,
    splitKind: input.splitKind,
    splits: splitExpense(input.amount, input.splitKind, input.participants),
    source: 'manual',
    sourceRef: null,
    createdAt: input.createdAt ?? input.spentAt,
  }
}

// ---------------------------------------------------------------------------
// Balances
// ---------------------------------------------------------------------------

export interface NetBalance {
  memberId: MemberId
  /** Positive: the ledger owes them. Negative: they owe the ledger. */
  amount: Piastres
}

export interface Ledger {
  /** Canonical pairwise nets. One row per pair, amount > 0, `from` owes `to`. */
  pairs: PairBalance[]
  /** Net position per member, including members who net to zero. */
  net: NetBalance[]
  /** Everyone with a ledger position, sorted. Guests are never here. */
  members: MemberId[]
}

export interface LedgerOptions {
  /**
   * Extra guard on top of `settledImmediately`. Anyone this returns true for is
   * excluded from every balance — guests carry no ongoing balance, ever.
   */
  isGuest?: (personId: PersonId) => boolean
  /** Only count expenses spent and settlements settled at or before this instant. */
  asOf?: number
}

interface Edge {
  from: MemberId
  to: MemberId
  amount: number
}

const pairKey = (a: MemberId, b: MemberId): string => (a < b ? `${a} ${b}` : `${b} ${a}`)

function buildLedger(edges: readonly Edge[], seedMembers: Iterable<MemberId>): Ledger {
  const debt = new Map<string, number>()
  const members = new Set<MemberId>(seedMembers)

  for (const e of edges) {
    members.add(e.from)
    members.add(e.to)
    if (e.from === e.to || e.amount === 0) continue
    const key = pairKey(e.from, e.to)
    const signed = e.from < e.to ? e.amount : -e.amount
    debt.set(key, (debt.get(key) ?? 0) + signed)
  }

  const pairs: PairBalance[] = []
  for (const [key, value] of debt) {
    if (value === 0) continue
    const [a, b] = key.split(' ') as [MemberId, MemberId]
    pairs.push(
      value > 0
        ? { from: a, to: b, amount: piastres(value) }
        : { from: b, to: a, amount: piastres(-value) },
    )
  }
  pairs.sort((x, y) => (x.from < y.from ? -1 : x.from > y.from ? 1 : x.to < y.to ? -1 : 1))

  const memberList = [...members].sort()
  const netMap = new Map<MemberId, number>(memberList.map(m => [m, 0]))
  for (const p of pairs) {
    netMap.set(p.from, (netMap.get(p.from) ?? 0) - p.amount)
    netMap.set(p.to, (netMap.get(p.to) ?? 0) + p.amount)
  }

  return {
    pairs,
    net: memberList.map(m => ({ memberId: m, amount: piastres(netMap.get(m) ?? 0) })),
    members: memberList,
  }
}

function ledgerEdges(
  expenses: readonly Expense[],
  settlements: readonly Settlement[],
  opts: LedgerOptions,
): { edges: Edge[]; members: Set<MemberId> } {
  const asOf = opts.asOf ?? Number.POSITIVE_INFINITY
  const isGuest = opts.isGuest
  /**
   * "Never carries an ongoing balance" (docs/01 §7.2) is a rule about the guest,
   * not about which column their id happens to sit in. `paidByMemberId` and a
   * settlement's endpoints are typed `MemberId`, so a guest can only reach them
   * through bad data — but a ledger is a fold that replays whatever synced, and
   * one such row would otherwise put a guest in `members`, `pairs` and `net`.
   * The guard is applied on every side, not only to split participants.
   */
  const guest = (m: MemberId): boolean => isGuest !== undefined && isGuest(personIdOf(m))
  const edges: Edge[] = []
  const members = new Set<MemberId>()

  for (const e of expenses) {
    if (e.spentAt > asOf) continue
    // An expense a guest paid for leaves nobody owing them: dropping the whole
    // row is the only reading under which the guest ends the day square.
    if (guest(e.paidByMemberId)) continue
    members.add(e.paidByMemberId)
    for (const s of e.splits) {
      // Guests appear in the split arithmetic and then disappear from the ledger.
      if (s.settledImmediately) continue
      if (isGuest !== undefined && isGuest(s.personId)) continue
      const debtor = memberIdOf(s.personId)
      members.add(debtor)
      if (debtor === e.paidByMemberId || s.shareAmount === 0) continue
      edges.push({ from: debtor, to: e.paidByMemberId, amount: s.shareAmount })
    }
  }

  for (const s of settlements) {
    if (s.settledAt > asOf) continue
    // A guest settles on the day; there is never an ongoing balance to settle.
    if (guest(s.fromMemberId) || guest(s.toMemberId)) continue
    members.add(s.fromMemberId)
    members.add(s.toMemberId)
    // Paying settles debt: it moves the pair back towards zero.
    edges.push({ from: s.toMemberId, to: s.fromMemberId, amount: s.amount })
  }

  return { edges, members }
}

/**
 * The pairwise ledger. With three people a pairwise matrix is clearer than
 * simplification (docs/01 §7.2) — `simplifyDebts` is offered as a view on top.
 *
 * Invariants:
 *  - the net positions always sum to exactly zero
 *  - given an `isGuest` predicate, no guest ever appears, in `pairs`, `net` or
 *    `members`, on ANY side of ANY row — payer, debtor or settlement endpoint
 *  - it never throws: a ledger is a fold over whatever synced, and one malformed
 *    row must not take the whole balance view down
 */
export function computeLedger(
  expenses: readonly Expense[],
  settlements: readonly Settlement[],
  opts: LedgerOptions = {},
): Ledger {
  const { edges, members } = ledgerEdges(expenses, settlements, opts)
  return buildLedger(edges, members)
}

/** How much `a` owes `b`. Negative means `b` owes `a`. */
export function pairBalance(ledger: Ledger, a: MemberId, b: MemberId): Piastres {
  for (const p of ledger.pairs) {
    if (p.from === a && p.to === b) return p.amount
    if (p.from === b && p.to === a) return piastres(-p.amount)
  }
  return ZERO
}

/** A member's net position. Positive: they are owed. */
export function netBalance(ledger: Ledger, m: MemberId): Piastres {
  for (const n of ledger.net) if (n.memberId === m) return n.amount
  return ZERO
}

/** Net positions implied by a set of pairwise balances. */
export function netFromPairs(pairs: readonly PairBalance[]): NetBalance[] {
  const net = new Map<MemberId, number>()
  for (const p of pairs) {
    net.set(p.from, (net.get(p.from) ?? 0) - p.amount)
    net.set(p.to, (net.get(p.to) ?? 0) + p.amount)
  }
  return [...net.keys()]
    .sort()
    .map(m => ({ memberId: m, amount: piastres(net.get(m) ?? 0) }))
}

// ---------------------------------------------------------------------------
// Settlements — append-only, reduce the balance rather than editing history
// ---------------------------------------------------------------------------

/**
 * Apply one settlement to an existing ledger, without touching history.
 *
 * Equivalent to recomputing the whole ledger with the settlement appended,
 * which is what makes settle-up append-only rather than destructive.
 */
export function applySettlementToLedger(ledger: Ledger, settlement: Settlement): Ledger {
  const edges: Edge[] = ledger.pairs.map(p => ({ from: p.from, to: p.to, amount: p.amount }))
  edges.push({
    from: settlement.toMemberId,
    to: settlement.fromMemberId,
    amount: settlement.amount,
  })
  return buildLedger(edges, ledger.members)
}

/** The payment that would exactly clear what `from` owes `to`. Zero if nothing is owed. */
export function amountToSettle(ledger: Ledger, from: MemberId, to: MemberId): Piastres {
  const owed = pairBalance(ledger, from, to)
  return owed > 0 ? owed : ZERO
}

/**
 * Minimum cash flow: the fewest transfers that clear the ledger.
 *
 * Greedy — repeatedly settle the largest debtor against the largest creditor,
 * ties broken by member id so every device produces the identical plan. Each
 * step zeroes at least one member, so it emits at most (non-zero members - 1)
 * transfers, and every member's net position is preserved exactly.
 */
export function simplifyDebts(pairs: readonly PairBalance[]): PairBalance[] {
  const net = new Map<MemberId, number>()
  for (const { memberId, amount } of netFromPairs(pairs)) {
    if (amount !== 0) net.set(memberId, amount)
  }

  const transfers: PairBalance[] = []
  for (;;) {
    let debtor: MemberId | null = null
    let debtorAmount = 0
    let creditor: MemberId | null = null
    let creditorAmount = 0
    for (const [m, v] of net) {
      if (v < 0) {
        if (debtor === null || v < debtorAmount || (v === debtorAmount && m < debtor)) {
          debtor = m
          debtorAmount = v
        }
      } else if (v > 0) {
        if (creditor === null || v > creditorAmount || (v === creditorAmount && m < creditor)) {
          creditor = m
          creditorAmount = v
        }
      }
    }
    if (debtor === null || creditor === null) break

    const amount = Math.min(-debtorAmount, creditorAmount)
    transfers.push({ from: debtor, to: creditor, amount: piastres(amount) })
    const d = debtorAmount + amount
    const c = creditorAmount - amount
    if (d === 0) net.delete(debtor)
    else net.set(debtor, d)
    if (c === 0) net.delete(creditor)
    else net.set(creditor, c)
  }

  transfers.sort((x, y) => (x.from < y.from ? -1 : x.from > y.from ? 1 : x.to < y.to ? -1 : 1))
  return transfers
}

/** Simplify a whole ledger into the settlements that would clear it. */
export const simplifyLedger = (ledger: Ledger): PairBalance[] => simplifyDebts(ledger.pairs)

// ---------------------------------------------------------------------------
// Food orders (docs/01 §7.3)
// ---------------------------------------------------------------------------

export interface FoodOrderLine {
  personId: PersonId
  /** What this person's own items cost. */
  itemsTotal: Piastres
  /** Their share of delivery + service. */
  feeShare: Piastres
  /** Their share of the discount, as a positive amount subtracted from the line. */
  discountShare: Piastres
  /** itemsTotal + feeShare - discountShare. */
  total: Piastres
  /** Guests settle on the day; their line never reaches the ledger. */
  settledImmediately: boolean
}

export interface FoodOrderSplitResult {
  /** Sum of every participant's items. */
  subtotal: Piastres
  /** deliveryFee + serviceFee. */
  fees: Piastres
  discount: Piastres
  /** subtotal + fees - discount. */
  total: Piastres
  lines: FoodOrderLine[]
}

export interface FoodOrderOptions {
  /** Which participants are guests. Guests split the bill but carry no balance. */
  isGuest?: (personId: PersonId) => boolean
}

/**
 * Split a food order across its participants.
 *
 * Fees split equally by default, or proportionally to each participant's order
 * value. The discount is always applied proportionally to order value. When
 * nobody ordered anything (subtotal zero) proportional degrades to equal, since
 * there is no order value to be proportional to.
 *
 * Every allocation is exact, so `sum(lines.total) === total` for every input.
 */
export function computeFoodOrderSplits(
  order: FoodOrder,
  opts: FoodOrderOptions = {},
): FoodOrderSplitResult {
  const parts = order.participants
  if (parts.length === 0) {
    throw new MoneyError('EMPTY_PARTICIPANTS', 'a food order needs at least one participant')
  }
  const seen = new Set<string>()
  for (const p of parts) {
    if (seen.has(p.personId)) {
      throw new MoneyError('DUPLICATE_PARTICIPANT', `${p.personId} appears twice in the order`)
    }
    seen.add(p.personId)
  }

  const itemTotals = parts.map(p => {
    let t = 0
    for (const item of p.items) {
      assertInt(item.price, `price of "${item.name}"`)
      if (item.price < 0) {
        throw new MoneyError('NEGATIVE_AMOUNT', `item "${item.name}" has a negative price`)
      }
      t += item.price
    }
    return piastres(t)
  })

  for (const [label, v] of [
    ['deliveryFee', order.deliveryFee],
    ['serviceFee', order.serviceFee],
    ['discount', order.discount],
  ] as const) {
    assertInt(v, label)
    if (v < 0) throw new MoneyError('NEGATIVE_AMOUNT', `${label} must not be negative`)
  }

  const subtotal = sumPiastres(itemTotals)
  const fees = piastres(order.deliveryFee + order.serviceFee)
  const discount = order.discount
  if (discount > subtotal + fees) {
    throw new MoneyError(
      'DISCOUNT_EXCEEDS_TOTAL',
      `discount ${discount} exceeds order total ${subtotal + fees}`,
    )
  }
  const total = piastres(subtotal + fees - discount)

  const byValue = (amount: Piastres): Piastres[] =>
    subtotal === 0
      ? allocateEqually(amount, parts.length)
      : allocateProportionally(amount, itemTotals)

  const feeShares: Piastres[] =
    order.feeSplitRule === 'equal' ? allocateEqually(fees, parts.length) : byValue(fees)
  const discountShares = byValue(discount)

  const isGuest = opts.isGuest
  const lines: FoodOrderLine[] = parts.map((p, i) => {
    const itemsTotal = itemTotals[i] as Piastres
    const feeShare = feeShares[i] as Piastres
    const discountShare = discountShares[i] as Piastres
    return {
      personId: p.personId,
      itemsTotal,
      feeShare,
      discountShare,
      total: piastres(itemsTotal + feeShare - discountShare),
      settledImmediately: isGuest !== undefined && isGuest(p.personId),
    }
  })

  return { subtotal, fees, discount, total, lines }
}

export interface FoodOrderExpenseOptions extends FoodOrderOptions {
  expenseId: ExpenseId
  /** Falls back to `order.paidByMemberId`. */
  paidByMemberId?: MemberId
  description?: string
  category?: string
  createdAt?: number
}

/**
 * Settling an order creates ONE expense with the computed splits, so lunch flows
 * into the same ledger as everything else (docs/01 §7.3). Guests' lines are
 * marked settled-immediately and never reach a balance.
 */
export function expenseFromFoodOrder(order: FoodOrder, opts: FoodOrderExpenseOptions): Expense {
  const payer = opts.paidByMemberId ?? order.paidByMemberId
  if (payer === null || payer === undefined) {
    throw new MoneyError('NO_PAYER', `food order ${order.id} has no payer`)
  }
  const computed = opts.isGuest === undefined
    ? computeFoodOrderSplits(order)
    : computeFoodOrderSplits(order, { isGuest: opts.isGuest })

  return {
    id: opts.expenseId,
    description: opts.description ?? `${order.restaurant} lunch`,
    amount: computed.total,
    spentAt: order.orderedAt,
    paidByMemberId: payer,
    category: opts.category ?? 'food',
    splitKind: 'exact',
    splits: computed.lines.map(l => ({
      personId: l.personId,
      shareAmount: l.total,
      settledImmediately: l.settledImmediately,
    })),
    source: 'food_order',
    sourceRef: order.id,
    createdAt: opts.createdAt ?? order.orderedAt,
  }
}
